import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { useAuthStore } from "./stores/auth.store";
import { useNotificationStore } from "./stores/notification.store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import oneSignalService from "./services/onesignal.service";
import { initAnalytics } from "./helpers/analytics.helper";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Organizations from "./pages/Organizations";
import OrganizationDetails from "./pages/OrganizationDetails";
import Users from "./pages/Users";
import UserEdit from "./pages/UserEdit";
import Report from "./pages/Report";
import BranchAnalysis from "./pages/BranchAnalysis";
import Notifications from "./pages/Notifications";
import Layout from "./components/Layout";
import AuthRoute from "./components/AuthRoute";
import {
  ORG_ADMIN_ROLES,
  PERM_APPROVAL_LIST,
  PERM_USER_LIST,
  PERM_USER_WRITE,
  USER_MANAGEMENT_ROLES,
} from "./auth";
import ApprovalsInbox from "./pages/approvals/ApprovalsInbox";
import { useAuthPermissions } from "./hooks/useAuthPermissions";
import HelpI18nProvider from "./components/help/HelpI18nProvider";
import AppI18nProvider from "./components/AppI18nProvider";
import KnowledgeBase from "./pages/support/KnowledgeBase";
import KnowledgeBaseArticle from "./pages/support/KnowledgeBaseArticle";
import "./i18n/help";
import { appI18n, getLanguageDir } from "./i18n/app";
import { APP_FONT_FAMILY } from "./constants/fonts";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";

const queryClient = new QueryClient();

// Separate emotion caches per direction so MUI/`sx` styles are physically
// mirrored (margins, padding, position, etc.) when an RTL language is active.
const ltrCache = createCache({ key: "mui", stylisPlugins: [prefixer] });
const rtlCache = createCache({
  key: "mui-rtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

function ApprovalsAuthRoute({ children }: { children: React.ReactNode }) {
  const { canViewApprovals } = useAuthPermissions();
  return (
    <AuthRoute
      orgScopedPermission={PERM_APPROVAL_LIST}
      legacyRoles={ORG_ADMIN_ROLES}
      accessCheck={canViewApprovals}
    >
      {children}
    </AuthRoute>
  );
}

const baseTheme = {
  typography: {
    fontFamily: APP_FONT_FAMILY,
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          padding: "8px 16px",
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderBottom: "1px solid",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "1px solid",
        },
      },
    },
  },
};

function App() {
  const { isAuthenticated, initAuth, tokenPayload } = useAuthStore();
  const isPilot = tokenPayload?.is_pilot === true;
  const oneSignalInitialized = useRef(false);
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const savedMode = localStorage.getItem("themeMode");
    return (
      (savedMode as "light" | "dark") ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light")
    );
  });

  const [direction, setDirection] = useState<"ltr" | "rtl">(() =>
    getLanguageDir()
  );

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", newMode);
      return newMode;
    });
  }, []);

  useEffect(() => {
    const onLanguageChanged = (lng: string) => setDirection(getLanguageDir(lng));
    appI18n.on("languageChanged", onLanguageChanged);
    return () => {
      appI18n.off("languageChanged", onLanguageChanged);
    };
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        ...baseTheme,
        direction,
        palette: {
          mode,
          primary: {
            main: "#7C3AED",
            light: "#A78BFA",
            dark: "#5B21B6",
            contrastText: "#FFFFFF",
          },
          secondary: {
            main: "#0EA5E9",
            light: "#38BDF8",
            dark: "#0284C7",
            contrastText: "#FFFFFF",
          },
          background: {
            default: mode === "dark" ? "#0F172A" : "#F1F5F9",
            paper: mode === "dark" ? "#1E293B" : "#FFFFFF",
          },
          text: {
            primary: mode === "dark" ? "#F8FAFC" : "#1E293B",
            secondary: mode === "dark" ? "#94A3B8" : "#64748B",
          },
          divider:
            mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.12)",
        },
        components: {
          ...baseTheme.components,
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                color: mode === "dark" ? "#F8FAFC" : "#1E293B",
                borderColor:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.06)",
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                borderColor:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "rgba(0, 0, 0, 0.06)",
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 16,
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                border:
                  mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.06)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow:
                  mode === "dark"
                    ? "0 4px 20px rgba(0, 0, 0, 0.2)"
                    : "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow:
                    mode === "dark"
                      ? "0 8px 30px rgba(0, 0, 0, 0.3)"
                      : "0 8px 24px rgba(0, 0, 0, 0.12)",
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: mode === "dark" ? "#1E293B" : "#FFFFFF",
                transition: "all 0.3s ease-in-out",
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                padding: "8px 16px",
                borderRadius: 12,
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                },
              },
              contained: {
                "&:hover": {
                  boxShadow: "0 4px 20px rgba(124, 58, 237, 0.4)",
                },
              },
            },
          },
        },
      }),
    [mode, direction]
  );

  useEffect(() => {
    initAnalytics();
    void initAuth();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || isPilot || oneSignalInitialized.current) return;
    oneSignalInitialized.current = true;
    oneSignalService.initialize();
  }, [isAuthenticated, isPilot]);

  // Handle notification clicks - mark as read in our system
  useEffect(() => {
    if (isAuthenticated && !isPilot) {
      const setupHandler = async () => {
        if (!oneSignalService.isReady()) {
          setTimeout(setupHandler, 1000);
          return;
        }

        const handleNotificationClick = async (notification: any) => {
          const notificationId = notification.id || notification.notificationId;
          
          const savedOrg = sessionStorage.getItem('selectedOrganization');
          let organizationId: string | null = null;
          
          if (savedOrg) {
            try {
              const parsedOrg = JSON.parse(savedOrg);
              organizationId = parsedOrg.id;
            } catch (e) {
              console.error('Error parsing saved organization:', e);
            }
          }
          
          if (organizationId && notificationId) {
            try {
              await useNotificationStore.getState().markAsRead(organizationId, notificationId);
            } catch (error) {
              console.error('Error marking notification as read:', error);
            }
          }
        };

        oneSignalService.setCustomClickHandler(handleNotificationClick);
      };

      setupHandler();
    }
  }, [isAuthenticated, isPilot]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppI18nProvider>
        <CacheProvider value={direction === "rtl" ? rtlCache : ltrCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
          <Routes>
            <Route
              path="/login"
              element={!isAuthenticated ? <Login /> : <Navigate to="/" />}
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Dashboard />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />

            <Route
              path="/organizations/:organizationId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <OrganizationDetails />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/organizations"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Organizations />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/users"
              element={
                <AuthRoute
                  orgScopedPermission={PERM_USER_LIST}
                  legacyRoles={USER_MANAGEMENT_ROLES}
                >
                  <Layout onThemeToggle={toggleTheme}>
                    <Users />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/approvals"
              element={
                <ApprovalsAuthRoute>
                  <Layout onThemeToggle={toggleTheme}>
                    <ApprovalsInbox />
                  </Layout>
                </ApprovalsAuthRoute>
              }
            />
            <Route
              path="/report"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Report />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/branch-analysis"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <BranchAnalysis />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/notifications"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <Notifications />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/users/new"
              element={
                <AuthRoute
                  orgScopedPermission={PERM_USER_WRITE}
                  legacyRoles={USER_MANAGEMENT_ROLES}
                >
                  <Layout onThemeToggle={toggleTheme}>
                    <UserEdit />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/users/:userId/edit"
              element={
                <AuthRoute
                  orgScopedPermission={PERM_USER_WRITE}
                  legacyRoles={USER_MANAGEMENT_ROLES}
                >
                  <Layout onThemeToggle={toggleTheme}>
                    <UserEdit />
                  </Layout>
                </AuthRoute>
              }
            />
            <Route
              path="/knowledge-base"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <HelpI18nProvider>
                      <KnowledgeBase />
                    </HelpI18nProvider>
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/knowledge-base/:articleId"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <HelpI18nProvider>
                      <KnowledgeBaseArticle />
                    </HelpI18nProvider>
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="*"
              element={
                isAuthenticated ? (
                  <Layout onThemeToggle={toggleTheme}>
                    <div />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            </Routes>
          </Router>
        </ThemeProvider>
        </CacheProvider>
      </AppI18nProvider>
    </QueryClientProvider>
  );
}

export default App;
